// @ts-nocheck
/* eslint-enable */
// App
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';

// Actions
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';

// Components
import AlertsButton from 'app/shared/modules/ListingHeader/AlertsButton';
import AllFiltersButtonBase from 'app/shared/modules/filter/FilterBar/AllFiltersButton/AllFiltersButtonBase';
import FilterButtonFullPageDropdown from 'app/shared/modules/MobileSRP/__BaseComponents__/FilterButtonFullPageDropdown';
import PriceFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/PriceFilter/component';
import BedBathComboFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/BedBathComboFilter/component';
import CommuteFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/CommuteFilter/component';
import PetFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/PetFilter/component';
import MoveInDateFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/MoveInDateFilter/component';
import AsyncAmenitiesFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/AmenitiesFilter';
import AsyncPropertyTypesFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/PropertyTypesFilter';
import AsyncSquareFootageFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/SquareFootageFilter';
import AsyncCreatedWithinFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/CreatedWithinFilter';
import AsyncRentalTypesFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/RentalTypesFilter';
import AsyncSpecialRestrictionsFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/SpecialRestrictionsFilter';
import AsyncKeywordsFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/KeywordsFilter';
import AsyncAdditionalOptionsFilter from 'app/shared/modules/MobileSRP/Header/FilterModules/AdditionalOptionsFilter';
import SpinnerDots from 'app/shared/modules/SpinnerDots';

// Hooks
import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';

// Misc / utils
import { filterUtils_getFilterLabels, filterUtils_hasActiveFilters } from 'app/shared/utils/filterUtils';
import IconFilter from 'images/icons/filter.svg';
import * as S from './styles';

const AllFiltersButton = ({ ariaButtonLabel, ariaDropdownLabel, onButtonClick, onModalClose, showDropdown }) => {
  // Redux
  const dispatch = useDispatch();
  const numDefaultFilterListings = useSelector((state) => state.listings.numDefaultFilterListings);
  const numFilteredListings = useSelector((state) => state.listings.totalListings);
  const filter = useSelector((state) => state.filter);

  // React state
  const [numListingsLoaded, setNumListingsLoaded] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // All filter buttons should have this ref to return focus after user has closed the filter modal
  const focusRef = useRef(null);
  const isLoaded = useRef(null);
  const prevFilterRef = useRef<string | null>(null);

  // Hooks
  const { resetFilter } = useFilterChange();

  useEffect(() => {
    /**
     * isLoaded is a component instance variable. Do not set focus on the button until
     * after the component has been interacted with.
     * @see: https://reactjs.org/docs/hooks-faq.html#is-there-something-like-instance-variables
     */
    if (!isLoaded.current) {
      isLoaded.current = true;
    } else {
      if (!showDropdown) {
        focusRef.current.focus();
      }
    }
  }, [showDropdown]);

  useEffect(() => {
    if (showDropdown && !numListingsLoaded) {
      dispatch(ListingEngineActions.fetchNumDefaultFilterListings({ isMapView: false })).then(() => {
        setNumListingsLoaded(true);
      });
    }
  }, [showDropdown, numListingsLoaded, dispatch]);

  useEffect(() => {
    if (showDropdown) {
      setHasActiveFilters(filterUtils_hasActiveFilters(filter));
    }
  }, [showDropdown, filter]);

  useEffect(() => {
    // Only fetch if dropdown is open and filter has actually changed (deep comparison)
    // Store stringified filter to ensure we compare by value, not reference
    const filterString = JSON.stringify(filter);
    const prevFilterString = prevFilterRef.current;
    const filterChanged = prevFilterString !== filterString;

    if (showDropdown && filterChanged) {
      prevFilterRef.current = filterString;
      setIsLoading(true);
      dispatch(ListingEngineActions.fetchNumberOfListings({ isMapView: false, filter })).then(() => {
        setIsLoading(false);
      });
    }
  }, [showDropdown, filter, dispatch]);

  const handleFullResetButtonClick = useYieldCallback(() => {
    resetFilter();
    onModalClose();
  });

  const dropdownHeader = (
    <>
      <S.HeaderArticle>
        <section>
          <S.AllFiltersMenuHeaderText>
            <S.NumFilteredListings>
              {isLoading ? <SpinnerDots /> : `${numFilteredListings} results`}
            </S.NumFilteredListings>
          </S.AllFiltersMenuHeaderText>
          {hasActiveFilters && <S.FilterLabels>{filterUtils_getFilterLabels(filter)}</S.FilterLabels>}
        </section>
        <section>
          {hasActiveFilters && (
            <S.ClearFiltersButton
              buttonType="text"
              onClick={handleFullResetButtonClick}
              aria-label={'Clear all active filters and close all filters modal'}
            >
              clear filters
            </S.ClearFiltersButton>
          )}
        </section>
      </S.HeaderArticle>
    </>
  );

  const dropdownLoadingHeader = <S.AllFiltersMenuHeaderText>Loading...</S.AllFiltersMenuHeaderText>;

  return (
    <>
      <AllFiltersButtonBase
        ariaLabel={ariaButtonLabel}
        ariaExpanded={showDropdown}
        icon={IconFilter}
        iconWidth="15px"
        iconHeight="15px"
        onClick={onButtonClick}
        customRef={focusRef}
      />
      {showDropdown && (
        <FilterButtonFullPageDropdown
          ariaLabel={ariaDropdownLabel}
          dropdownId={'all-filters-dropdown'}
          onClose={onModalClose}
          header={numListingsLoaded ? dropdownHeader : dropdownLoadingHeader}
        >
          <S.AlertButtonSection>
            <S.AlertText>Alert me on new properties or price changes based on my filters</S.AlertText>
            <AlertsButton />
          </S.AlertButtonSection>
          {numListingsLoaded && (
            <>
              <PriceFilter />
              <BedBathComboFilter triggerLocation="search_results" triggerObject="filter_panel" />
              <CommuteFilter />
              <PetFilter />
              <MoveInDateFilter />
              <AsyncAmenitiesFilter />
              <AsyncPropertyTypesFilter />
              <AsyncSquareFootageFilter />
              <AsyncCreatedWithinFilter />
              <AsyncRentalTypesFilter />
              <AsyncSpecialRestrictionsFilter />
              <AsyncKeywordsFilter />
              <AsyncAdditionalOptionsFilter />
            </>
          )}
        </FilterButtonFullPageDropdown>
      )}
    </>
  );
};

export default AllFiltersButton;
